/**
 * Community chat room — the "Public room" tab of the chat panel.
 *
 * Opt-in, per the RFC: nothing is joined until the user presses "Join room".
 * Joining resolves the room id and holds it on the app-wide chat socket as
 * PUBLIC_ROOM_HOLDER; `useHeldRoom` then loads (and after a reconnect,
 * re-syncs) the latest history, and live messages arrive through chatSlice.
 *
 * The hold outlives this component: switching chat tabs or closing the panel
 * unmounts it without leaving, and remounting renders the cached thread
 * directly. Only "Leave room" releases it.
 *
 * The thread and composer are shared with the Support tab (ChatThread,
 * ChatComposer); this component owns only joining and leaving.
 */
import { useState } from "react";
import { useChat, useHeldRoom } from "@/hooks/useChatSocket";
import { useAppDispatch, useAppSelector } from "@/state/hooks";
import { fetchPublicChatRoom } from "@/state/slices/chatSlice";
import { ChatComposer } from "./ChatComposer";
import { ChatThread } from "./ChatThread";
import { errMsg } from "./chatErrors";
import styles from "./PublicRoom.module.css";

/** This component's name as a room holder (see chatSlice `roomHolders`). */
export const PUBLIC_ROOM_HOLDER = "public-room";

function HashIcon({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden="true">
      <path d="M4 9h16M4 15h16M10 3 8 21M16 3l-2 18" />
    </svg>
  );
}

export function PublicRoom() {
  const dispatch = useAppDispatch();
  const { joinRoom, leaveRoom } = useChat();
  const roomId = useAppSelector((s) => s.chat.publicRoomId);
  // The user's choice (held until they press Leave); the socket-level
  // membership is chatSlice `joinedRoomIds`.
  const { held } = useHeldRoom(roomId, PUBLIC_ROOM_HOLDER);

  const [joining, setJoining] = useState(false);
  const [joinError, setJoinError] = useState<string | null>(null);

  const handleJoin = async () => {
    setJoining(true);
    setJoinError(null);
    try {
      const id = roomId ?? (await dispatch(fetchPublicChatRoom()).unwrap());
      // useHeldRoom loads the history once the hold lands.
      joinRoom(id, PUBLIC_ROOM_HOLDER);
    } catch (e) {
      setJoinError(`Couldn't open the room: ${errMsg(e)}`);
    } finally {
      setJoining(false);
    }
  };

  if (!held || !roomId) {
    return (
      <div className={styles.joinCard}>
        <div className={styles.joinIcon}>
          <HashIcon size={26} />
        </div>
        <h3 className={styles.joinTitle}>Public room</h3>
        <p className={styles.joinCopy}>
          Chat with other Pulsarix traders and the support team. Joining is optional — you
          can leave anytime.
        </p>
        <button
          type="button"
          className={styles.joinButton}
          onClick={() => void handleJoin()}
          disabled={joining}
        >
          {joining ? "Joining…" : "Join room"}
        </button>
        {joinError ? (
          <p className={styles.error} role="alert">
            {joinError}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div className={styles.room}>
      <div className={styles.roomBar}>
        <div>
          <b className={styles.roomName}>Public room</b> · all users + supporters
        </div>
        <button
          type="button"
          className={styles.leaveButton}
          onClick={() => leaveRoom(roomId, PUBLIC_ROOM_HOLDER)}
        >
          Leave room
        </button>
      </div>
      <ChatThread roomId={roomId} emptyText="No messages yet — say hello." />
      <ChatComposer
        roomId={roomId}
        inputId="public-room-input"
        placeholder="Message the community…"
      />
    </div>
  );
}
